import type { GrowthDemoInfo } from './growthDemo';

export type GrowthSubview =
  | 'sources'
  | 'overview'
  | 'website'
  | 'guests'
  | 'local'
  | 'campaigns'
  | 'health'
  | 'setup'
  // "Your Google accounts": outside any restaurant's context (docs/19 section 5).
  | 'accounts';

/** The detail views a step of the path opens (WP4.6: only ones a click reaches). */
export type GrowthDive =
  | 'gsc-origin'
  | 'gsc-click'
  | 'gbp-origin'
  | 'gbp-click'
  | 'site'
  | 'landed'
  | 'widget';

export type GrowthFacet =
  | 'pages'
  | 'queries'
  | 'device'
  | 'country'
  | 'channels';

export const GROWTH_FACETS: GrowthFacet[] = [
  'pages',
  'queries',
  'device',
  'country',
  'channels',
];

const GROWTH_DIVE_ALIASES: Record<string, GrowthDive> = {
  'gsc-origin': 'gsc-origin',
  'gsc-click': 'gsc-click',
  'gbp-origin': 'gbp-origin',
  'gbp-click': 'gbp-click',
  site: 'site',
  landed: 'landed',
  widget: 'widget',
  gsc: 'gsc-origin',
  gbp: 'gbp-origin',
  ga4: 'site',
};

/**
 * Dives that only a typed URL could reach, retired in WP4.6. Their old links
 * open the section that now answers the same question: Ads campaigns,
 * reservations and the money they bring are in Sources; the
 * placeholder "origin" and "click" were steps of the path.
 */
const RETIRED_DIVES: Record<string, GrowthSubview> = {
  // "Seen on Google" and "Clicked through" are Local and search now (WP5.1).
  origin: 'local',
  click: 'local',
  'ads-origin': 'sources',
  'ads-click': 'sources',
  ads: 'sources',
  booked: 'sources',
  bookings: 'sources',
  value: 'sources',
};

/** The section an old dive link opens instead, or null when it is no retired dive. */
export function retiredGrowthDiveSection(raw: string | null | undefined): GrowthSubview | null {
  return raw ? RETIRED_DIVES[raw] ?? null : null;
}

/** Dives into a Google product: they live in Local and search (WP5.1). */
const LOCAL_DIVES: ReadonlySet<GrowthDive> = new Set<GrowthDive>(['gsc-origin', 'gsc-click', 'gbp-origin', 'gbp-click']);

/** The section a dive belongs to: Google products in Local and search, the website in Website and reservation. */
export function growthDiveSection(dive: GrowthDive | null | undefined): GrowthSubview | null {
  if (!dive) return null;
  return LOCAL_DIVES.has(dive) ? 'local' : 'website';
}

/**
 * `?demo=tracify` opens the Tracify demo (A5): the demo is reached through
 * Data health or a link, never from the model menu. The value stands in for
 * the demo's first model until the person picks another.
 */
export const TRACIFY_DEMO_LINK = 'tracify';

/**
 * Growth's sections in tab order (concept section 14, Sources first).
 * Website and Local and search show only when their Google source is
 * connected (`sectionAvailability`); the rest always.
 */
export const GROWTH_ANALYTIC_SUBVIEWS: Exclude<GrowthSubview, 'setup' | 'accounts'>[] = [
  'sources',
  'overview',
  'guests',
  // Campaigns and events (WP5.5): their own methods, no Google report.
  'campaigns',
  'website',
  'local',
  'health',
];

/** Sections that need a Google source; hidden while `sectionAvailability` says it is missing. */
export const GROWTH_CONNECTED_SUBVIEWS: ReadonlySet<GrowthSubview> = new Set<GrowthSubview>(['website', 'local']);

export const GROWTH_SUBVIEWS: GrowthSubview[] = [...GROWTH_ANALYTIC_SUBVIEWS, 'setup'];

export const DEFAULT_GROWTH_SUBVIEW: GrowthSubview = 'sources';

/** Links saved before the section rethink keep opening the nearest section. */
const RETIRED_SUBVIEWS: Record<string, GrowthSubview> = {
  pulse: 'overview',
  restaurants: 'overview',
  // Path to reservation is Website and reservation (WP5.2).
  path: 'website',
  funnel: 'website',
  schema: 'website',
  paid: 'website',
  organic: 'website',
  // Google products moved to Local and search (WP5.1).
  gbp: 'local',
  search: 'local',
  journeys: 'website',
  // Channels merged into Sources (concept section 14).
  channels: 'sources',
  reservations: 'sources',
  xray: 'sources',
  events: 'campaigns',
  // Reports were removed (15 Oct 2026): their links open the default section.
  reports: DEFAULT_GROWTH_SUBVIEW,
  report: DEFAULT_GROWTH_SUBVIEW,
};

export function resolveGrowthSubview(raw: string | null | undefined): GrowthSubview {
  if (!raw) return DEFAULT_GROWTH_SUBVIEW;
  if ((GROWTH_SUBVIEWS as string[]).includes(raw)) return raw as GrowthSubview;
  if (raw === 'accounts') return 'accounts';
  return RETIRED_SUBVIEWS[raw] ?? DEFAULT_GROWTH_SUBVIEW;
}

export function resolveGrowthDive(raw: string | null | undefined): GrowthDive | null {
  if (!raw) return null;
  return GROWTH_DIVE_ALIASES[raw] ?? null;
}

export function resolveGrowthFacet(raw: string | null | undefined): GrowthFacet | null {
  if (!raw) return null;
  return (GROWTH_FACETS as string[]).includes(raw) ? (raw as GrowthFacet) : null;
}

export function diveHop(
  dive: GrowthDive | null | undefined,
): 'origin' | 'click' | 'site' | 'landed' | 'widget' | null {
  if (!dive) return null;
  if (dive === 'gsc-origin' || dive === 'gbp-origin') return 'origin';
  if (dive === 'gsc-click' || dive === 'gbp-click') return 'click';
  return dive;
}

export type GrowthProvenance = 'observed' | 'joined' | 'modeled' | 'synthetic';

export type GrowthMetricUnit = 'count' | 'ratio' | 'currency' | 'reservations' | 'covers';

export type GrowthUnavailableReason =
  | 'not_connected'
  | 'needs_mapping'
  | 'needs_reauth'
  | 'syncing'
  | 'no_data'
  | 'error'
  | 'quality_blocked'
  | 'context_only'
  | 'no_exact_match'
  | 'no_denominator'
  | 'mixed_currency'
  | 'pos_unavailable'
  | 'no_coverage'
  | 'no_view_set'
  | 'no_conversion_signal'
  | 'unavailable'
  // WP2.1: Google fetched live; why a figure has no number this time.
  | 'offline'
  | 'timeout'
  | 'incomplete'
  | 'history_limit'
  | 'not_requested'
  // WP3.7: a store read failed, or the connection list could not be read.
  | 'source_error'
  | 'connections_unavailable'
  | 'shared_key';

export type GrowthMetric = {
  value: number | null;
  unit?: GrowthMetricUnit;
  available?: boolean;
  unavailableReason?: GrowthUnavailableReason | string | null;
  provenance: GrowthProvenance;
  confidence?: string;
  source?: string;
  formula?: string;
  coverage?: number | null;
  coverageDetail?: {
    sourceIds?: string[];
    coveredRestaurantIds?: string[];
    coveredCount?: number;
    scopeCount?: number;
    coverageRatio?: number | null;
    explanation?: string | null;
  };
  currency?: string | null;
  freshness?: string | null;
  contextOnly?: boolean;
  /** Google's data-quality warnings on this figure (WP2.5). */
  notes?: string[];
  /** The definition this figure implements (WP3.4); see payload.definitions. */
  metricId?: GrowthMetricId;
};

/** Metric ids of the dictionary (growth_definitions.py, WP3.4). */
export type GrowthMetricId =
  | 'online_reservations'
  | 'online_booked'
  | 'covers_served'
  | 'cancellation_rate'
  | 'no_show_rate'
  | 'channel_known_share'
  | 'lead_time'
  | 'new_guests_group'
  | 'new_guests_venue'
  | 'new_guest_share_group'
  | 'new_guest_share_venue'
  | 'returning_share_group'
  | 'returning_share_venue'
  | 'guest_unknown_share'
  | 'second_visit_rate_90'
  | 'visits_per_guest_180'
  | 'covers_per_guest_180'
  | 'pos_revenue_per_guest_180'
  | 'lapsed_regulars'
  | 'pos_revenue'
  | 'reservation_rate'
  | 'sessions'
  | 'visitors'
  | 'last_click_reservations'
  | 'not_matched_reservations'
  | 'profile_actions'
  // Local and search (WP5.1).
  | 'profile_views'
  | 'profile_reservations'
  | 'branded_search_share'
  | 'non_branded_clicks'
  | 'ads_impressions'
  | 'ads_clicks'
  | 'ads_click_rate'
  // Website and reservation (WP5.2).
  | 'page_step_sessions'
  | 'widget_opens'
  | 'event_step_count'
  | 'website_reservations'
  | 'step_per_100_sessions'
  | 'no_availability_searches'
  // Campaigns and events (WP5.5).
  | 'campaign_reservations'
  | 'campaign_covers'
  | 'campaign_new_guests'
  | 'campaign_lift'
  | 'service_fill';

/** One definition per metric (WP3.4), in the person's language. */
export type GrowthDefinition = {
  id: GrowthMetricId | string;
  label: string;
  definition: string;
  availableWhen: string;
  unit: string;
  numerator: string;
  denominator: string | null;
  /** False for distinct guests and medians: never added up across venues. */
  additive: boolean;
  sources: string[];
  provenance: GrowthProvenance;
  block: string;
};

/** A headline figure with the counts it is built from (WP3.4). */
export type GrowthKpi = GrowthMetric & { numerator?: number | null; denominator?: number | null };

export type GrowthKpiScope = Partial<Record<GrowthMetricId, GrowthKpi>>;

export type GrowthKpis = {
  current: { all: GrowthKpiScope; byRestaurant: Record<string, GrowthKpiScope> } | null;
  prior: { all: GrowthKpiScope; byRestaurant: Record<string, GrowthKpiScope> } | null;
};

export type GrowthSourceStatus =
  | 'not_connected'
  | 'needs_mapping'
  | 'needs_reauth'
  | 'syncing'
  | 'no_data'
  | 'ready'
  | 'error'
  // WP1.10: connection problems, each with copy and an action.
  | 'product_not_granted'
  | 'access_removed'
  | 'account_inactive'
  | 'api_not_enabled'
  | 'rate_limited'
  // WP2.1: live fetch outcomes.
  | 'offline'
  | 'timeout'
  | 'incomplete'
  | 'history_limit'
  | 'not_requested'
  // WP3.7: Growth could not read which connections exist; not "not connected".
  | 'connections_unavailable';

export type GrowthSource = {
  id: 'ga4' | 'ads' | 'gsc' | 'gbp' | 'pos' | string;
  label: string;
  status: GrowthSourceStatus;
  lastRefresh?: number | null;
  sessionLive?: boolean | null;
  action?: { id: string; label: string | null };
  detail?: string;
  error?: string | null;
  /**
   * Who connected this data: the aleno user, never the Google account (WP1.11).
   * `departed`: that person no longer has access (filtering audit 7.1, rule 5).
   */
  connectedBy?: { name?: string | null; isStaff?: boolean | null; departed?: boolean } | null;
  /** A connection problem while data still shows (WP1.10), or "stale":
   * Google is limiting requests and the numbers are from an earlier fetch. */
  issue?: {
    status: GrowthSourceStatus | 'stale';
    detail: string;
    action?: { id: string; label: string | null };
  } | null;
  /** When the numbers on show were fetched from Google (ms), if this view fetched them. */
  fetchedAt?: number | null;
  /** The last day Google's data covers (YYYY-MM-DD). */
  dataThrough?: string | null;
  /** True when the numbers come from an earlier fetch because Google failed or limited us. */
  stale?: boolean;
  /**
   * POS only: whether any venue of the org has a POS (null when unknown).
   * Status `not_connected` means none does; `no_data` that the venues in
   * view link no revenue.
   */
  orgHasPos?: boolean | null;
};

export type GrowthSourceCoverage = {
  id: 'ga4' | 'ads' | 'gsc' | 'gbp' | 'pos' | string;
  label?: string;
  status?: GrowthSourceStatus | string;
  coveredRestaurantIds?: string[];
  coveredCount?: number;
  scopeCount?: number;
  coverageRatio?: number | null;
  resourceIds?: string[];
  sharedRestaurantIds?: string[];
  ambiguousRestaurantIds?: string[];
  excludedResourceIds?: string[];
  /**
   * Venues whose figures this source feeds: their own property, or one they
   * share with other venues in view. `coveredRestaurantIds` stays "own
   * property only" for per-venue joins.
   */
  reachedRestaurantIds?: string[];
  reachedCount?: number;
  /** Of them, venues reached only through a shared property. */
  reachedSharedCount?: number;
  /** Properties feeding the view that also cover venues outside it (rule 2). */
  beyondViewResourceIds?: string[];
};

/**
 * A shared property feeding the view (filtering audit 7.1, rule 2): its
 * figures count every venue it covers, as one aggregate that cannot be split.
 * `restaurantIds` are the covered venues the viewer can open; the others are
 * only counted.
 */
export type GrowthSharedCoverage = {
  resourceId: string;
  kind: 'ga4' | 'ads' | 'gsc' | 'gbp' | string;
  restaurantIds: string[];
  inViewRestaurantIds: string[];
  othersCount: number;
  coversCount: number;
};

/** A connection whose connector no longer has access (rule 5). */
export type GrowthDepartedConnection = {
  grantId: string;
  name?: string | null;
  since: number;
  stopsAt: number;
  stopped: boolean;
  kinds: string[];
};

export type GrowthRestaurantSourceState =
  | string
  | {
      status?: string;
      detail?: string | null;
      resourceIds?: string[];
      shared?: boolean;
      ambiguous?: boolean;
      overlapRisk?: boolean;
    };

export type GrowthRestaurantMetrics = Partial<
  Record<
    | 'sessions'
    | 'adsClicks'
    | 'searchImpressions'
    | 'searchClicks'
    | 'profileViews'
    | 'profileActions',
    (Partial<GrowthMetric> & { value?: number | null }) | number | null
  >
>;

export type GrowthRestaurant = {
  restaurantId: string;
  /** Optional in v7; the client catalog remains the name authority. */
  name?: string | null;
  sourceStates?: Partial<
    Record<'ga4' | 'ads' | 'gsc' | 'gbp' | 'pos' | string, GrowthRestaurantSourceState>
  >;
  metrics?: GrowthRestaurantMetrics;
};

export type GrowthResourceContribution = {
  resourceId?: string;
  externalId?: string;
  displayName?: string;
  kind?: string;
  restaurantIds?: string[];
  scopedRestaurantIds?: string[];
  outsideScopeRestaurantIds?: string[];
  includedInTotals?: boolean;
  shared?: boolean;
  overlapRisk?: boolean | string;
  ambiguousRestaurantIds?: string[];
  exclusionReason?: string | null;
  metrics?:
    | GrowthRestaurantMetrics
    | Record<string, (Partial<GrowthMetric> & { value?: number | null }) | number | null>;
  pages?: GrowthPage[];
  queries?: GrowthQuery[];
  /** WP5.1: this site's top searches that do not name the business (live only). */
  nonBrandedQueries?: GrowthQuery[];
  campaigns?: GrowthCampaign[];
  searchTerms?: GrowthSearchTerm[];
  device?: GrowthBreakdownRow[];
  country?: GrowthBreakdownRow[];
};

export type GrowthStage = {
  id: string;
  label: string;
  metric: GrowthMetric;
  rateFromPrev?: GrowthMetric;
  guidance?: GrowthGuideStep[];
};

export type GrowthGuideStep = {
  n: number;
  title: string;
  body: string;
  href?: string;
};

export type GrowthLane = {
  id: string;
  contextOnly?: boolean;
  currency?: string | null;
  stages?: GrowthStage[];
  pages?: GrowthPage[];
  impressions?: GrowthMetric;
  impressionsMaps?: GrowthMetric;
  impressionsSearch?: GrowthMetric;
  conversations?: GrowthMetric;
  websiteClicks?: GrowthMetric;
  calls?: GrowthMetric;
  directions?: GrowthMetric;
  directBookings?: GrowthMetric;
  guidance?: GrowthGuideStep[];
};

export type GrowthPage = {
  landingPath: string;
  impressions: GrowthMetric;
  clicks: GrowthMetric;
  ctr: GrowthMetric;
  position?: GrowthMetric;
};

export type GrowthQuery = {
  query: string;
  impressions: GrowthMetric;
  clicks: GrowthMetric;
  ctr: GrowthMetric;
  position?: GrowthMetric;
};

export type GrowthSearchTerm = {
  term: string;
  campaign?: string;
  campaignId?: string | null;
  impressions: GrowthMetric;
  clicks: GrowthMetric;
};

export type GrowthLanding = {
  landingPath: string;
  sessions: GrowthMetric;
  widgetOpened?: GrowthMetric;
  ga4Reservations?: GrowthMetric;
};

export type GrowthLandingChannel = {
  landingPath: string;
  channelGroup: string;
  sessions: GrowthMetric;
  widgetOpened?: GrowthMetric;
  ga4Reservations?: GrowthMetric;
};

export type GrowthBreakdownRow = {
  id: string;
  label: string;
  sessions?: GrowthMetric;
  impressions?: GrowthMetric;
  clicks?: GrowthMetric;
  engagedSessions?: GrowthMetric;
};

export type GrowthFunnelHops = {
  widgetOpened?: GrowthMetric;
  engagedSessions?: GrowthMetric;
  users?: GrowthMetric;
};

export type GrowthCampaign = {
  key: string;
  label: string;
  type?: string;
  impressions: GrowthMetric;
  clicks: GrowthMetric;
  reservations: GrowthMetric;
  servedCovers: GrowthMetric;
  matched?: boolean;
  matchKind?: string | null;
  impressionShare?: number;
};

export type GrowthQualityGate = {
  id: string;
  ok: boolean;
  severity: string;
  title: string;
  detail: string;
  blocks?: string[];
};

export type GrowthChannelId =
  | 'paid_search'
  | 'organic_search'
  | 'local_listings'
  | 'reserve_with_google'
  | 'direct'
  | 'referral'
  | 'organic_social'
  | 'paid_social'
  | 'email'
  | 'ai_search'
  | 'other'
  | string;

export type GrowthChannelSource = {
  source: string;
  medium: string;
  sessions: number;
};

/**
 * One channel's website traffic, as Google counts it (contract 42): no
 * aleno reservation figure. A channel's reservations are the Sources
 * X-ray's, counted by outcome.
 */
export type GrowthChannel = {
  id: GrowthChannelId;
  label: string;
  rank: number;
  primarySource: string;
  sessionShare: number | null;
  topSources: GrowthChannelSource[];
  sessions: GrowthMetric;
  widgetOpened: GrowthMetric;
  /** Analytics' own reservation event: its count, not aleno's. */
  ga4Reservations: GrowthMetric;
  searchImpressions: GrowthMetric;
  searchClicks: GrowthMetric;
  adsImpressions: GrowthMetric;
  adsClicks: GrowthMetric;
  profileImpressions: GrowthMetric;
  profileWebsiteClicks: GrowthMetric;
  profileCalls: GrowthMetric;
  profileDirections: GrowthMetric;
  profileBookings: GrowthMetric;
};

export type GrowthAttributionGap = {
  id: string;
  title: string;
  detail: string;
  benefits: string[];
  /** The work that closes the gap, carried with the finding itself. */
  steps?: string[];
  href?: string | null;
  action?: string | null;
};

/**
 * "Channel known" (channel_known_share) and the measurement gaps. The counts
 * are completed online reservations (widget and Reserve with Google) from
 * the channel-quality query, and those whose channel is known: Reserve with
 * Google, or a widget reservation with UTM tags (WP3.4); null when it was
 * not read.
 */
export type GrowthAttribution = {
  channelKnownMetricId?: string;
  onlineReservations: number | null;
  channelKnownReservations: number | null;
  channelKnownShare: number | null;
  gaps: GrowthAttributionGap[];
};

export type GrowthCoverage = {
  inScope: number;
  mapped: number;
  unmapped: number;
  unmappedSample?: string[];
  partial?: boolean;
  sharedBrandNote?: boolean;
  contextOnly?: boolean;
  mappedTotal?: number;
  mappedOutsideScope?: number;
  mappedOutsideSample?: string[];
  /**
   * Google resources of this org kept out of the view (outside the venues,
   * shared with a venue outside, or overlapping), as counts only.
   */
  excluded?: Array<{ kind: string; reason: string; count: number }>;
};

/**
 * Booked widget reservations per website session (metric reservation_rate),
 * for the venues with an Analytics property of their own.
 *
 * The only conversion rate that survives a property with no event tracking:
 * aleno counts the booked widget reservations (completed, cancelled or
 * no-show, as the website trend and funnel do), Analytics counts the
 * sessions. ``bookings`` and ``sessions`` are its numerator and denominator.
 */
export type GrowthSiteConversion = {
  rate: GrowthMetric;
  bookings: GrowthMetric;
  sessions: GrowthMetric;
  /** Booked Reserve with Google reservations of the same venues: never on the site. */
  excludedReserveWithGoogle: number | null;
  /** The rate is too high to be only the measured site — worth checking. */
  offsiteLikely: boolean;
  /** The same rate for the comparison window, same restaurants (WP3.1). */
  prior?: { rate: GrowthMetric; sessions: GrowthMetric; bookings: GrowthMetric };
};

/** What the figures are compared with (WP3.1, decision D4). */
export type GrowthCompare = {
  kind: 'off' | 'prior' | 'yoy' | 'yoy_weekday' | 'custom' | string;
  /** False when Growth chose it: the same weeks last year by weekday. */
  explicit: boolean;
  alignment: 'weekday' | 'date' | 'previous' | 'custom' | null;
  /** "vs same weeks last year (by weekday)", in the person's language. */
  label: string;
  /** The comparison dates, e.g. "2 August – 30 August 2025". */
  range: string;
  dateStart: string | null;
  dateEnd: string | null;
  shiftDays: number | null;
};

/** How far the period has happened (WP3.1). */
export type GrowthPeriod = {
  dateStart: string;
  /** The last complete day counted; null when not one day is complete yet. */
  dateEnd: string | null;
  /** The end the person asked for. */
  requestedEnd: string;
  /** The period reaches today: figures stop at yesterday in the venues' time. */
  inProgress: boolean;
  elapsedDays: number;
  label: string;
};

/** Optional property analytics split. Fields stay optional for rolling deploys. */
export type GrowthPropertyBreakdown = {
  resourceId?: string;
  externalId?: string;
  displayName?: string;
  kind?: 'ga4' | string;
  restaurantIds?: string[];
  scopedRestaurantIds?: string[];
  directRestaurantId?: string | null;
  shared?: boolean;
  overlapRisk?: boolean | string;
  includedInTotals?: boolean;
  status?: string | null;
  exclusionReason?: string | null;
  channels?: GrowthChannel[];
  channelTotals?: Partial<GrowthPayload['channelTotals']>;
  attribution?: Partial<GrowthAttribution> | null;
  siteConversion?: Partial<GrowthSiteConversion> | null;
  revenue?: Record<string, GrowthMetric>;
  landings?: GrowthLanding[];
  landingChannels?: GrowthLandingChannel[];
  funnelHops?: GrowthFunnelHops;
};

export type GrowthBlockUnavailable =
  | 'off'
  | 'no_view_set'
  | 'unsupported'
  | 'source_error'
  | 'no_window';

/**
 * One window's POS side (contract 43): the POS revenue of the Sources
 * X-ray's online rows (checks linked to completed widget and Reserve with
 * Google reservations, by service date), for the view and each venue with
 * POS checks in the window. Reservation counts are in ``kpis`` and
 * ``series``, each by its definition.
 */
export type GrowthComparisonSide = {
  totals: { posRevenue: number | null; currency: string | null };
  byRestaurant: Record<string, { posRevenue?: number | null; reason?: string }>;
  posAvailable: boolean;
  /** Why there is no POS revenue: no_pos, no_pos_linked, mixed_currency, not_cached, ... */
  reason?: string | null;
};

export type GrowthComparison = {
  kind: string;
  label: string;
  dateStart: string | null;
  dateEnd: string | null;
  available: boolean;
  reason?: GrowthBlockUnavailable;
  prior?: GrowthComparisonSide;
  current: GrowthComparisonSide | null;
};

export type GrowthChannelGroupId =
  | 'paid_search'
  | 'paid_social'
  | 'paid_shopping'
  | 'paid_video'
  | 'display'
  | 'paid_other'
  | 'cross_network'
  | 'organic_search'
  | 'organic_social'
  | 'organic_video'
  | 'organic_shopping'
  | 'influencer'
  | 'newsletter'
  | 'email'
  | 'local_listings'
  | 'reserve_with_google'
  | 'own_website'
  | 'referral'
  | 'affiliates'
  | 'qr_code'
  | 'ai_search'
  | 'direct'
  | 'audio'
  | 'sms'
  | 'mobile_push'
  | 'unassigned'
  | 'untagged'
  | 'phone_staff'
  | 'ai_phone'
  | 'other'
  | 'walk_in'
  | 'other';

/**
 * One cell of the channel-quality query. Booked reservations only: `booked`
 * is completed + cancelled + no-show; success counts (completed, covers,
 * guests) are over completed reservations (growth-concept section 8).
 */
export type GrowthChannelQualityRow = {
  period: 'current' | 'prior';
  restaurantId: string;
  source: string;
  subKey: string | null;
  medium: string | null;
  channelGroup: GrowthChannelGroupId | string;
  campaign: string | null;
  booked: number;
  completed: number;
  cancelled: number;
  noShow: number;
  /** Covers of the completed reservations, and of the cancelled and no-show ones. */
  coversCompleted: number;
  coversLost: number;
  /** Completed reservations with a known guest id; the denominator for new-guest share. */
  known: number;
  /** Completed reservations without a guest id ("guest unknown"). */
  unknown: number;
  /** Completed reservations by guests new to the group / the venue. */
  newGroup: number;
  newVenue: number;
};

export type GrowthQualityLevel =
  | 'total'
  | 'venue'
  | 'channel'
  | 'venue_channel'
  | 'tag'
  | 'venue_tag'
  | 'campaign'
  | 'venue_campaign';

/** What does not add up across rows, computed per level (WP3.4). */
export type GrowthQualitySummaryRow = {
  period: 'current' | 'prior';
  level: GrowthQualityLevel;
  restaurantId: string | null;
  channelGroup: string | null;
  subKey: string | null;
  medium: string | null;
  campaign: string | null;
  booked: number;
  completed: number;
  known: number;
  unknown: number;
  newGroup: number;
  newVenue: number;
  /** Distinct guests new to the group / the venue. */
  newGuestsGroup: number;
  newGuestsVenue: number;
  leadDaysMedian: number | null;
  leadDaysCount: number;
};

export type GrowthChannelQuality =
  | {
      available: true;
      hasPrior: boolean;
      truncated: boolean;
      /** The periods whose rows hit the cap; each period is capped on its own (WP3.7). */
      truncatedPeriods?: Array<'current' | 'prior'>;
      rows: GrowthChannelQualityRow[];
      summary?: GrowthQualitySummaryRow[];
      historyDays: number;
    }
  | { available: false; reason: GrowthBlockUnavailable };

export type GrowthCohortRow = {
  cohort: string;
  restaurantId: string;
  /** Absent for a section request: the cells add up over source and sub-channel (WP6.2). */
  source?: string;
  subKey?: string | null;
  channelGroup: GrowthChannelGroupId | string;
  guests: number;
  mature30: number;
  returned30: number;
  mature60: number;
  returned60: number;
  mature90: number;
  returned90: number;
  mature180: number;
  visits180: number;
  covers180: number;
};

export type GrowthGuestDefinition = {
  returnWindows: number[];
  valueDays: number;
  lapsed: { minVisits: number; lookbackDays: number; afterDays: number };
  historyDays: number;
};

/**
 * The win-back export (opsCommand/exportLapsedRegulars, WP5.3): CSV with the
 * aleno guest id, venue, visits and last visit; no name, email or phone.
 */
export type GrowthLapsedExport = {
  csv: string;
  count: number;
  /** More guests than one file holds; the most recent are in it. */
  truncated: boolean;
  asOf: string;
  columns: string[];
  venues: number;
};

/** POS revenue of mature new guests over 180 days, per venue, first channel and currency (WP5.3). */
export type GrowthGuestValueRow = {
  restaurantId: string;
  channelGroup: GrowthChannelGroupId | string;
  /** Null when the POS sent no currency. */
  currency: string | null;
  /** New guests with at least one linked POS basket in their first 180 days. */
  linkedGuests: number;
  revenue: number;
};

export type GrowthGuestValue =
  | {
      available: true;
      valueDays: number;
      /** Venues in view whose POS links baskets to reservations. */
      posVenues: string[];
      rows: GrowthGuestValueRow[];
      prior?: { rows: GrowthGuestValueRow[] } | null;
    }
  | { available: false; reason: 'no_pos' | 'unsupported' | 'source_error' | string; posVenues?: string[] };

export type GrowthGuests =
  | {
      available: true;
      asOf: string;
      cohortStart: string;
      cohortEnd: string;
      /** The largest cohort cells up to a cap (WP3.7). */
      truncated?: boolean;
      cohorts: GrowthCohortRow[];
      lapsed: Array<{ restaurantId: string; lapsed: number; visits: number }>;
      definition: GrowthGuestDefinition;
      /** Guest value by first channel; only when the Guests section asked (WP5.3). */
      value?: GrowthGuestValue;
      /** The same twelve months of cohorts, a comparison earlier (WP3.1). */
      prior?: {
        asOf: string;
        cohortStart: string;
        cohortEnd: string;
        cohorts: GrowthCohortRow[];
        lapsed: Array<{ restaurantId: string; lapsed: number; visits: number }>;
      } | null;
    }
  | { available: false; reason: GrowthBlockUnavailable; definition?: GrowthGuestDefinition };

export type GrowthSeriesRow = {
  period: 'current' | 'prior';
  /** Visit date, or the Monday of the ISO week when `grain` is week. */
  bucket: string;
  restaurantId: string;
  /** Completed reservations of every source but walk-ins. */
  completed: number;
  /** Completed online reservations, and every booked one (completed, cancelled, no-show). */
  online: number;
  onlineBooked: number;
  /** Completed widget reservations with a UTM tag. */
  tagged: number;
  /** Widget reservations made (booked): the website's conversion count. */
  widget: number;
  /** Covers of completed online reservations. */
  covers: number;
  noShow: number;
  cancelled: number;
};

export type GrowthSeries =
  | {
      available: true;
      grain: 'day' | 'week';
      start: string;
      end: string;
      priorStart: string | null;
      priorEnd: string | null;
      truncated: boolean;
      truncatedPeriods?: Array<'current' | 'prior'>;
      rows: GrowthSeriesRow[];
    }
  | { available: false; reason: GrowthBlockUnavailable };

export type GrowthBlockName =
  | 'queries'
  | 'nonBrandedQueries'
  | 'searchTerms'
  | 'pages'
  | 'landings'
  | 'landingChannels'
  | 'gaDevice'
  | 'gaCountry'
  | 'gscDevice'
  | 'gscCountry'
  | 'adsDevice';

/**
 * One Google resource's figures for Local and search (WP5.1): the period
 * and the comparison window. A side is null when Google had nothing to give,
 * with the reason; a single figure is null when Google did not send it.
 */
export type GrowthLocalRow<T> = {
  resourceId: string;
  displayName?: string | null;
  /** The venues the resource is mapped to (all inside the view). */
  restaurantIds: string[];
  current: T | null;
  reason: string | null;
  prior: T | null;
  priorReason: string | null;
};

export type GrowthProfileFigures = {
  views: number | null;
  viewsSearch: number | null;
  viewsMaps: number | null;
  calls: number | null;
  directions: number | null;
  websiteClicks: number | null;
  bookings: number | null;
  conversations: number | null;
  actions: number | null;
};

export type GrowthSearchFigures = {
  brandedClicks: number | null;
  brandedImpressions: number | null;
  nonBrandedClicks: number | null;
  nonBrandedImpressions: number | null;
  /** Every click and impression Search Console counted, named searches or not. */
  clicks: number | null;
  impressions: number | null;
  /** Google sent its maximum of 25,000 searches: the split may miss some. */
  truncated: boolean;
};

export type GrowthAdsFigures = { impressions: number | null; clicks: number | null };

export type GrowthAdsCampaign = {
  resourceId: string;
  key: string;
  /** Verbatim, as named in Google Ads. */
  label: string;
  type: string;
  current: { impressions: number | null; clicks: number | null; impressionShare: number | null };
  prior: { impressions: number | null; clicks: number | null } | null;
};

export type GrowthLocalSeriesRow = {
  resourceId: string;
  period: 'current' | 'prior';
  bucket: string;
  views: number;
  actions: number;
};

export type GrowthLocalSearch = {
  compareKind: string;
  profiles: {
    status: GrowthSourceStatus | string;
    rows: Array<GrowthLocalRow<GrowthProfileFigures>>;
    series: {
      grain: 'day' | 'week';
      start: string;
      end: string;
      priorStart: string | null;
      priorEnd: string | null;
      rows: GrowthLocalSeriesRow[];
    } | null;
  };
  search: {
    status: GrowthSourceStatus | string;
    /** The org's brand terms: configuration, never Google data. */
    brandTerms: string[];
    brandTermsSource: 'saved' | 'suggested' | 'none' | 'error' | 'not_requested' | string;
    brandTermsEditable: boolean;
    suggestedTerms: string[];
    rows: Array<GrowthLocalRow<GrowthSearchFigures>>;
  };
  ads: {
    status: GrowthSourceStatus | string;
    rows: Array<GrowthLocalRow<GrowthAdsFigures>>;
    campaigns: GrowthAdsCampaign[];
    truncated: boolean;
  };
  /** The top searches that do not name the business: live only, never stored. */
  nonBrandedQueries: GrowthQuery[];
};

/** A website step (WP5.2): sessions and reservations are fixed; the steps
 * between them are set per Analytics property in Data health. */
export type GrowthWebsiteStep = {
  id: string;
  type: 'sessions' | 'pages' | 'event' | 'reservations';
  /** Typed by the venue; default steps carry a preset and are shown translated. */
  name?: string;
  preset?: 'menu' | 'widget' | null;
  source?: 'default' | 'saved' | 'mixed';
  /** pages: the address parts of every property that has the step. */
  parts?: string[];
  /** event: the event names of every property that has the step. */
  events?: string[];
  properties?: number;
  /** Properties in view without this step: it then has no total. */
  missing?: number;
  /** The step counts the widget-open event (the widget's exits show under it). */
  widget?: boolean;
  conflict?: boolean;
};

/** One side (period or comparison) of the website steps. */
export type GrowthWebsiteSide = {
  steps: Record<string, GrowthMetric>;
  /** Each step per 100 sessions (unit per_100_sessions), never against the step before. */
  per100: Record<string, GrowthMetric>;
  /** Widget searches that found no free table: the widget's own event. */
  noAvailability: GrowthMetric;
  /** Widget opens minus reservations in aleno minus found no availability. */
  left: GrowthMetric;
};

export type GrowthFunnelBreakdownRow = {
  id: string;
  steps?: Record<string, number | null>;
  /** Venue rows: "ok", or why the venue has no Analytics figures of its own. */
  status?: string;
  /** Venue rows: the venue's own Analytics property. */
  resourceId?: string;
  /** Venue rows: why a step has no number for this venue. */
  reasons?: Record<string, string>;
  noAvailability?: number | null;
  prior?: { steps?: Record<string, number | null>; noAvailability?: number | null };
};

export type GrowthFunnelBreakdownId = 'device' | 'landing' | 'channel' | 'venue';

export type GrowthFunnelBreakdown = {
  available: boolean;
  reason: string | null;
  rows: GrowthFunnelBreakdownRow[];
  /** Steps this breakdown cannot count, with the reason. */
  unavailable?: Record<string, string>;
  truncated?: boolean;
};

/** The website steps block; the payload key stays websiteFunnel. */
export type GrowthWebsiteFunnel = {
  available: boolean;
  reason: string | null;
  /** matched: venues with their own Analytics property; unmatched: none can be joined. */
  basis: 'matched' | 'unmatched' | 'none';
  venues: string[];
  scopeCount: number;
  propertyIds: string[];
  steps?: GrowthWebsiteStep[];
  stepsSource?: 'default' | 'saved' | 'mixed';
  events: Partial<Record<'widgetOpened' | 'reservationCreated' | 'availabilityNoSlots', string[]>>;
  compareKind: string;
  /** Who counts reservations in each breakdown: aleno, or Analytics' reservation event. */
  reservedSources: Record<GrowthFunnelBreakdownId, 'ga4' | 'aleno'>;
  current?: GrowthWebsiteSide;
  prior?: GrowthWebsiteSide | null;
  priorReason?: string | null;
  noAvailability?: { seen: boolean; step?: string | null };
  breakdowns?: Record<GrowthFunnelBreakdownId, GrowthFunnelBreakdown>;
  series?: {
    grain: 'day' | 'week';
    start: string;
    end: string;
    priorStart: string | null;
    priorEnd: string | null;
    /** The trend's metric choices: sessions, the event steps, reservations. */
    metrics?: string[];
    /** Steps without a trend, with the reason (pages steps: no_daily_pages). */
    unavailable?: Record<string, string>;
    rows: Array<{ resourceId: string; period: 'current' | 'prior'; bucket: string; sessions: number; steps?: Record<string, number> }>;
  } | null;
};

export type GrowthGoogleComparison = {
  kind: string;
  label: string;
  dateStart: string | null;
  dateEnd: string | null;
  available: boolean;
  reason?: string | null;
  sources: Partial<
    Record<
      'ga4' | 'ads' | 'gsc' | 'gbp',
      Record<string, GrowthMetric | number | string | null | undefined>
    >
  >;
};

/**
 * The view context's statuses, sources and shifts as Growth applied them
 * (decision 7.5.2). Absent without filters. ``outcomes`` says which outcomes
 * the status filter leaves in: an excluded one is left out, never zero.
 */
export type GrowthFiltersBlock = {
  statuses: string[];
  sources: string[];
  shifts: string[];
  outcomes: {
    completed: 'all' | 'some' | 'excluded';
    cancelled: 'all' | 'excluded';
    noShow: 'all' | 'excluded';
  };
  /** Chosen statuses that are no outcome on Growth (waitlist, rejected). */
  notCounted: string[];
  matchesNothing: boolean;
  /** Google's figures are never narrowed by aleno's filters. */
  google: 'not_filtered';
};

export type GrowthPayload = {
  contractVersion: number;
  /** The filters the reservation figures follow (decision 7.5.2). */
  filters?: GrowthFiltersBlock;
  /**
   * Set when Growth read nothing because the venues on screen are more than
   * one payload holds (decision 7.7.1): the person is told, never shown fewer.
   */
  scopeLimit?: { reason: 'too_many_venues'; max: number; venues: number };
  /** Only on the demo group's payload: what to label it and its venues. */
  demo?: GrowthDemoInfo;
  /** What the figures are compared with (WP3.1). */
  compare?: GrowthCompare;
  /** One definition per metric, keyed by id (WP3.4). */
  definitions?: Record<string, GrowthDefinition>;
  /** Headline figures per scope, current and comparison (WP3.4). */
  /** Null for a section that shows no headline figure (WP6.2). */
  kpis?: GrowthKpis | null;
  /** Store reads that failed, by block, with why; the rest renders (WP3.7). */
  blockErrors?: Partial<
    Record<'connections' | 'campaignUtmMap' | 'brandTerms' | 'migration', string>
  >;
  /**
   * "group" when the org's venues share guest ids, so a guest can be new to
   * the group; "venue" when each venue keeps its own (WP3.5).
   */
  guestIdentity?: 'group' | 'venue';
  guestIdentityDetail?: {
    identity: 'group' | 'venue';
    venues: number;
    guests: number | null;
    sharedGuests: number | null;
    sharedShare: number | null;
    reason: string | null;
  };
  /** How far the period has happened (WP3.1). */
  period?: GrowthPeriod;
  series?: GrowthSeries;
  comparison?: GrowthComparison;
  channelQuality?: GrowthChannelQuality;
  guests?: GrowthGuests;
  status: string;
  message?: string | null;
  periodLabel: string;
  dateStart?: string | null;
  dateEnd?: string | null;
  coverage: GrowthCoverage;
  sourcesConnected: string[];
  sources?: GrowthSource[];
  sourceCoverage?: GrowthSourceCoverage[];
  /** Shared properties feeding the view, labelled with every venue they cover. */
  sharedCoverage?: GrowthSharedCoverage[];
  /** The restaurant group's name, for "2 more of ButchersTable's venues". */
  customerName?: string | null;
  /** Connections whose connector no longer has access (rule 5). */
  departedConnections?: GrowthDepartedConnection[];
  restaurants?: GrowthRestaurant[];
  resourceContributions?: GrowthResourceContribution[];
  propertyBreakdowns?: GrowthPropertyBreakdown[];
  attribution: GrowthAttribution;
  /** The website's traffic per channel, Google's counts only (contract 42). */
  channels: GrowthChannel[];
  channelTotals: {
    sessions: number | null;
  };
  siteConversion?: GrowthSiteConversion;
  lanes: {
    paid: GrowthLane;
    organic: GrowthLane;
    local: GrowthLane;
  };
  revenue: Record<string, GrowthMetric>;
  pages: GrowthPage[];
  queries?: GrowthQuery[];
  searchTerms?: GrowthSearchTerm[];
  /**
   * Blocks that cannot be shown right now, keyed by payload field, with a
   * reason code (GrowthUnavailableReason). Google free text (queries,
   * search terms) is fetched per request and never cached; "not_requested"
   * when the section did not ask for it.
   */
  unavailableBlocks?: Partial<Record<GrowthBlockName, GrowthUnavailableReason | string>>;
  /** The sections this payload was built for (WP2.8). */
  sections?: string[];
  /** Sources first (concept section 14): the section Growth opens on. */
  defaultSection?: string;
  /** Which sections have something to show (Website needs Analytics, Local a Google product). */
  sectionAvailability?: Record<string, GrowthSectionAvailability>;
  /** The Sources X-ray (docs/growth-sources-contract.md). */
  sourcesXray?: GrowthSourcesXray;
  /** Whose clock each figure follows (WP2.4). */
  timezones?: {
    venue: string;
    venues: string[];
    sources: Partial<Record<'ga4' | 'ads' | 'gsc', string[]>>;
    aligned: boolean;
  };
  /** GA4's data-quality warnings per block (WP2.5): other_row, thresholded, sampled, truncated. */
  dataNotes?: Partial<Record<string, string[]>>;
  /** Google totals for the comparison window, fetched live (WP2.1; shown from WP3.1). */
  googleComparison?: GrowthGoogleComparison;
  /** Business Profile, branded search and Ads reach per resource (WP5.1). */
  localSearch?: GrowthLocalSearch;
  /** Website and reservation: the column funnel and its breakdowns (WP5.2). */
  websiteFunnel?: GrowthWebsiteFunnel;
  landings?: GrowthLanding[];
  landingChannels?: GrowthLandingChannel[];
  breakdowns?: {
    gaDevice?: GrowthBreakdownRow[];
    gaCountry?: GrowthBreakdownRow[];
    gscDevice?: GrowthBreakdownRow[];
    gscCountry?: GrowthBreakdownRow[];
    adsDevice?: GrowthBreakdownRow[];
  };
  funnelHops?: GrowthFunnelHops;
  quality: GrowthQualityGate[];
  guidance: Record<string, GrowthGuideStep[]>;
  freshness: {
    lastSuccessfulRefresh?: number | null;
    cachedThrough?: string | null;
    /** The oldest Google numbers on show (ms) and how far Google's data reaches (WP2.6). */
    fetchedAt?: number | null;
    dataThrough?: string | null;
    sessionLive?: boolean;
    rowCount?: number;
    /** What this build asked Google for (WP2.8). */
    fetch?: {
      calls?: Record<string, number>;
      durationMs?: number;
      ga4Tokens?: number;
      cacheHits?: number;
      fetchedKinds?: number;
    };
  };
  viewSet?: string[];
  resources?: Array<{
    resourceId: string;
    kind: string;
    displayName: string;
    externalId: string;
    syncStatus?: string;
    /** The venues it feeds that the viewer can open (rule 2). */
    restaurantIds?: string[];
    /** Every venue it feeds, those the viewer cannot open included. */
    coversCount?: number;
    lastError?: string | null;
    warnings?: string[];
    /** When a live fetch of this resource last worked (ms). */
    lastFetchedAt?: number | null;
    eventConfig?: Record<string, string>;
    /** GA4: the website steps Data health edits (saved, or derived by default). */
    websiteSteps?: {
      steps: Array<{ id: string; name: string; type: 'pages' | 'event'; parts?: string[]; event?: string; preset?: 'menu' | 'widget' }>;
      source: 'default' | 'saved';
      maxSteps: number;
      maxParts: number;
    } | null;
    campaignCatalog?: Array<{ campaignId?: string; campaignName?: string }>;
  }>;
  auths?: Array<{
    authId: string;
    kind: string;
    status: string;
    isOwner?: boolean;
    tokenExpiresAt?: number;
  }>;
};

export const GROWTH_SOURCE_TO_SETUP: Record<string, 'ga' | 'googleads' | 'gsc' | 'gbp'> = {
  ga4: 'ga',
  ads: 'googleads',
  gsc: 'gsc',
  gbp: 'gbp',
};

// --- Sources X-ray (growth-concept section 14; docs/growth-sources-contract.md) ---

export type GrowthSectionAvailability = {
  available: boolean;
  reason: string | null;
  /** Google products any one of which makes the section available. */
  needs: string[];
};

export type GrowthSourceLevel = 'all' | 'source' | 'channel' | 'utm' | 'campaign';

/** A node of the drill path; levels below the node are null. */
export type GrowthSourceNodeKey = {
  source: string | null;
  channel: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  campaign: string | null;
};

/** Count arrays aligned with `GrowthSourcesXray.fields`; null when the node had no reservation then. */
export type GrowthSourceCounts = (number | null)[] | null;

export type GrowthSourceNode = GrowthSourceNodeKey & {
  /** "all", or the node's path as a JSON array string. */
  id: string;
  parent: string | null;
  level: GrowthSourceLevel;
  label: string;
  /** The children of `parent` past the cap, added up (additive fields only). */
  tail: boolean;
  tailNodes?: number;
  current: GrowthSourceCounts;
  prior: GrowthSourceCounts;
};

/** A venue's node: the view's node with the same id, or (rarely) a full node. */
export type GrowthSourceVenueNode =
  | { id: string; current: GrowthSourceCounts; prior: GrowthSourceCounts }
  | GrowthSourceNode;

export type GrowthSourceMetricSpec = {
  id: string;
  metricId: string;
  view: 'volume' | 'guests' | 'quality' | 'value';
  unit: string;
  /** A count field, `a-b`, `a+b`, or `completed@all` (the whole scope's completed reservations). */
  numerator: string;
  denominator: string | null;
  minBase: number | null;
  baseField?: string;
};

export type GrowthSourceHeadlineFigure = {
  metricId: string;
  unit: string;
  available: boolean;
  reason: string | null;
  current: number | null;
  prior: number | null;
  currency?: string | null;
};

export type GrowthSourceLayerRow = {
  id: string;
  reason: string | null;
  [figure: string]: string | number | null;
};

export type GrowthSourceLayer = {
  available: boolean;
  reason: string | null;
  source?: string;
  notes?: string[];
  rows?: GrowthSourceLayerRow[];
  unmatched?: Record<string, string | number | null>[];
  unmatchedCount?: number;
};

export type GrowthSourceAttributionLayer = {
  available: boolean;
  reason: string | null;
  provider: 'tracify';
  mode: 'live' | null;
  demoAvailable: boolean;
  join: { channel: string; campaign: string };
  lastClickField: string;
};

export type GrowthSourcesXray = {
  available: boolean;
  reason: string | null;
  levels?: GrowthSourceLevel[];
  fields?: string[];
  tailFields?: string[];
  guestIdentity?: 'group' | 'venue';
  hasPrior?: boolean;
  metrics?: GrowthSourceMetricSpec[];
  revenue?: {
    available: boolean;
    reason: string | null;
    currency: string | null;
    byRestaurant: Record<string, { currency: string | null; reason: string | null }>;
  };
  truncated?: boolean;
  truncatedLevels?: GrowthSourceLevel[];
  childrenCap?: { view: number; venue: number };
  backDays?: number;
  historyDays?: number;
  minRateBase?: number;
  nodes?: GrowthSourceNode[];
  byRestaurant?: Record<string, { nodes: GrowthSourceVenueNode[] }>;
  headline?: Record<string, GrowthSourceHeadlineFigure>;
  layers?: {
    analytics: GrowthSourceLayer;
    ads: GrowthSourceLayer;
    attribution: GrowthSourceAttributionLayer;
  };
};

/** The request of getGrowthSourceDetail / getGrowthSourceReservations / exportGrowthSourceReservations. */
export type GrowthSourceRequest = {
  node?: string | Partial<GrowthSourceNodeKey>;
  /** The view context's venue filter (decision 7.7.1); absent is the restaurant opened. */
  regionFilter?: string;
  /** The view context's filters, as getGrowth takes them (decision 7.5.2). */
  status?: string[];
  source?: string[];
  shift?: string[];
  restaurantIds?: string[];
  settings?: Record<string, unknown>;
  page?: number;
  pageSize?: number;
  sort?: 'desc' | 'asc';
};

export type GrowthSourceDetailEntry = {
  key: string | null;
  current: GrowthSourceCounts;
  prior: GrowthSourceCounts;
  label?: string | null;
  restaurantId?: string;
  utmSource?: string;
  utmMedium?: string;
  tail?: boolean;
  tailKeys?: number;
};

export type GrowthSourceDetail = {
  available: boolean;
  reason: string | null;
  node?: GrowthSourceNodeKey;
  nodeId?: string;
  level?: GrowthSourceLevel;
  label?: string;
  fields?: string[];
  tailFields?: string[];
  metrics?: GrowthSourceMetricSpec[];
  guestIdentity?: 'group' | 'venue';
  hasPrior?: boolean;
  grain?: 'day' | 'week';
  total?: { current: GrowthSourceCounts; prior: GrowthSourceCounts };
  trend?: { period: 'current' | 'prior'; bucket: string; values: GrowthSourceCounts }[];
  venues?: GrowthSourceDetailEntry[];
  daypart?: GrowthSourceDetailEntry[];
  weekday?: GrowthSourceDetailEntry[];
  leadTime?: GrowthSourceDetailEntry[];
  partySize?: GrowthSourceDetailEntry[];
  guests?: { group: GrowthSourceDetailEntry[]; venue: GrowthSourceDetailEntry[] };
  channels?: GrowthSourceDetailEntry[];
  utm?: GrowthSourceDetailEntry[];
  campaigns?: GrowthSourceDetailEntry[];
  leadBuckets?: string[];
  partyBuckets?: string[];
  dayparts?: string[];
  dateStart?: string;
  dateEnd?: string;
  period?: GrowthPeriod;
  compare?: GrowthCompare;
  restaurantIds?: string[];
  demo?: GrowthDemoInfo;
};

export type GrowthSourceReservation = {
  reservationId: string;
  serviceDate: string | null;
  serviceTime: string | null;
  restaurantId: string;
  venue: string;
  covers: number;
  status: string;
  source: string;
  integration: string | null;
  channel: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  guest: 'new' | 'returning' | 'unknown';
  bookedAheadDays: number | null;
  posRevenue: number | null;
  currency: string | null;
};

export type GrowthSourceReservations = {
  available: boolean;
  reason: string | null;
  node: GrowthSourceNodeKey;
  nodeId: string;
  label: string;
  page: number;
  pageSize: number;
  pages?: number;
  sort: 'desc' | 'asc';
  total: number;
  listed?: number;
  truncated?: boolean;
  guestIdentity: 'group' | 'venue';
  revenue?: { available: boolean; reason: string | null };
  columns: string[];
  rows: GrowthSourceReservation[];
  dateStart?: string;
  dateEnd?: string;
  period?: GrowthPeriod;
  compare?: GrowthCompare;
  restaurantIds?: string[];
  demo?: GrowthDemoInfo;
};

export type GrowthSourceReservationsExport = {
  available: boolean;
  reason: string | null;
  csv: string;
  count: number;
  total: number | null;
  truncated: boolean;
  columns: string[];
  nodeId: string | null;
  label: string | null;
  dateStart?: string;
  dateEnd?: string;
  demo?: boolean;
};
